/** Occasion map portals: memorials, celebrations, and free workshop maps. */

export type OccasionType = 'celebration' | 'memorial' | 'workshop';

export const MEMORIES_HOSTS = [
  'memories.theoverhear.app',
  'memories-theoverhear.web.app',
] as const;

export const CELEBRATIONS_HOSTS = [
  'celebrate.theoverhear.app',
  'celebrate-theoverhear.web.app',
] as const;

export const DEFAULT_OCCASION_RADIUS_M = 30;

export function getHostname(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname;
}

/** True when serving on the memories consumer host (or a hostname containing "memories"). */
export function isMemoriesDomain(hostname = getHostname()): boolean {
  return (
    MEMORIES_HOSTS.includes(hostname as (typeof MEMORIES_HOSTS)[number]) ||
    hostname.includes('memories')
  );
}

/** True when serving on the celebration consumer host. */
export function isCelebrationsDomain(hostname = getHostname()): boolean {
  return (
    CELEBRATIONS_HOSTS.includes(hostname as (typeof CELEBRATIONS_HOSTS)[number]) ||
    hostname.includes('celebrate') ||
    hostname.includes('celebrations')
  );
}

/** True when on either occasion consumer subdomain. */
export function isOccasionDomain(hostname = getHostname()): boolean {
  return isMemoriesDomain(hostname) || isCelebrationsDomain(hostname);
}

/**
 * Resolves occasion type from hostname first, then from a path prefix
 * (`/memories`, `/celebrations`) for local/admin development.
 */
export function resolveOccasionType(options?: {
  hostname?: string;
  pathname?: string;
}): OccasionType | null {
  const hostname = options?.hostname ?? getHostname();
  if (isMemoriesDomain(hostname)) {
    return 'memorial';
  }
  if (isCelebrationsDomain(hostname)) {
    return 'celebration';
  }

  const pathname = options?.pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (pathname.startsWith('/memories')) {
    return 'memorial';
  }
  if (pathname.startsWith('/celebrations')) {
    return 'celebration';
  }
  return null;
}

/** Canonical public origin for contribute / marketing links for an occasion type. */
export function getOccasionPortalOrigin(occasionType: OccasionType): string {
  if (typeof window !== 'undefined') {
    const { origin, hostname } = window.location;
    if (
      (occasionType === 'memorial' && isMemoriesDomain(hostname)) ||
      (occasionType === 'celebration' && isCelebrationsDomain(hostname))
    ) {
      return origin;
    }
    // Local / admin: keep current origin so path aliases work
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
      return origin;
    }
  }
  if (occasionType === 'workshop') {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'https://admin.theoverhear.app';
  }
  return occasionType === 'memorial'
    ? 'https://memories.theoverhear.app'
    : 'https://celebrate.theoverhear.app';
}

/** Guest contribute URL for a token. */
export function buildOccasionContributeUrl(occasionType: OccasionType, token: string): string {
  return `${getOccasionPortalOrigin(occasionType)}/contribute/${encodeURIComponent(token)}`;
}

/** Organiser manage URL for a created map. */
export function buildOccasionMapUrl(occasionType: OccasionType, projectId: string): string {
  return `${getOccasionPortalOrigin(occasionType)}/map/${encodeURIComponent(projectId)}`;
}

/** Landing path for path-based local/admin access. */
export function getOccasionLandingPath(occasionType: OccasionType): string {
  if (occasionType === 'workshop') {
    return '/workshop-maps';
  }
  return occasionType === 'memorial' ? '/memories' : '/celebrations';
}

/** True on the public celebrate / memorial landing pages (not create, maps, or collect). */
export function isOccasionLandingPath(
  pathname: string,
  hostname = getHostname(),
): boolean {
  const path =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (isOccasionDomain(hostname)) {
    return path === '/' || path === '';
  }
  return path === '/memories' || path === '/celebrations';
}

/** Create-wizard path (hostname-locked on production; path-prefixed on local/admin). */
export function getOccasionCreatePath(occasionType: OccasionType, hostname = getHostname()): string {
  if (occasionType === 'workshop') {
    return '/workshop-maps';
  }
  if (isOccasionDomain(hostname)) {
    return '/create';
  }
  return occasionType === 'memorial' ? '/memories/create' : '/celebrations/create';
}

/** Organiser maps list path. */
export function getOccasionMapsPath(occasionType: OccasionType, hostname = getHostname()): string {
  if (occasionType === 'workshop') {
    return '/workshop-maps';
  }
  if (isOccasionDomain(hostname)) {
    return '/maps';
  }
  return occasionType === 'memorial' ? '/memories/maps' : '/celebrations/maps';
}

/** Manage URL for a single occasion map (relative path). */
export function getOccasionManagePath(projectId: string): string {
  return `/map/${encodeURIComponent(projectId)}`;
}

export function buildCelebrationHeadline(names: string): string {
  const trimmed = names.trim();
  return trimmed ? `Congratulations ${trimmed}` : 'Congratulations';
}

export function buildWorkshopHeadline(names: string): string {
  const trimmed = names.trim();
  return trimmed || 'Workshop map';
}

export function buildMemorialHeadline(name: string, dates?: string): string {
  const trimmedName = name.trim();
  const trimmedDates = dates?.trim();
  if (!trimmedName) {
    return 'In Memory';
  }
  if (trimmedDates) {
    return `In Memory of ${trimmedName} ${trimmedDates}`;
  }
  return `In Memory of ${trimmedName}`;
}

/** Admin stores memorial dates both in the headline and honoreeDates. */
export function splitOccasionHeadlineAndDates(
  headline?: string | null,
  honoreeDates?: string | null,
): { headline: string; dates?: string } {
  const title = headline?.trim() ?? '';
  const dates = honoreeDates?.trim();
  if (!dates) {
    return { headline: title };
  }
  if (title.endsWith(dates)) {
    return {
      headline: title.slice(0, title.length - dates.length).trim(),
      dates,
    };
  }
  return { headline: title, dates };
}

/** Token format: occ_{memorial|celebration|workshop}_{random}. */
export function generateOccasionContributeToken(occasionType: OccasionType): string {
  const random = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  return `occ_${occasionType}_${random}`;
}

/** Unambiguous alphabet for human-entered claim codes (no 0/O/1/I). */
const CLAIM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * Short uppercase claim code recipients type into the OVERHEAR app.
 * Format: 6 characters from an unambiguous alphabet.
 */
export function generateOccasionClaimCode(length = 6): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let code = '';
  for (let i = 0; i < length; i += 1) {
    code += CLAIM_CODE_ALPHABET[bytes[i]! % CLAIM_CODE_ALPHABET.length];
  }
  return code;
}

/** Normalise user-entered claim codes for lookup. */
export function normaliseOccasionClaimCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Recipient collect page URL (share this — not the OneLink directly). */
export function buildOccasionCollectPageUrl(
  occasionType: OccasionType,
  claimCode: string
): string {
  return `${getOccasionPortalOrigin(occasionType)}/collect/${encodeURIComponent(
    normaliseOccasionClaimCode(claimCode)
  )}`;
}
